// VIS-267 lighter pictures (the owner's pick "Smaller", 2026-10-24: "So visually, I see no difference"): the material
// maps the browser paints (materials.js boards, logs, shingles, metal, snow; lazy/matlook.js torched, poles) come baked
// in the package as KTX2, colour ETC1S and data UASTC with mips (tools/bake_material_maps.mjs <id>): about a quarter
// of the graphics memory, and nothing to paint at the start. A picture is named by the hash of its recipe (the
// generator, its arguments and its seed), so equal recipes share one file and one texture (a second ask for a recipe
// gets the same textures, painted or baked). The package's painted/maps.json lists the baked recipes; a recipe it does
// not list (a Fine-tune edit, a new material) or whose file does not load is painted as before: a cache, not a switch.
// A bump map is never baked (block compression bands its slopes: the bake leaves out a recipe used as a bumpMap).
import * as THREE from 'three/webgpu';
import { freeOnUpload } from './gpu/release.js';

export const BAKED_DIR = 'painted/';
export const bakedFile = (key, role) => `${key}-${role}.ktx2`;

/** A recipe's name: FNV-1a over its JSON in two lanes (two primes), 16 hex digits. */
export function recipeKey(recipe) {
  const s = JSON.stringify(recipe);
  let a = 0x811c9dc5, b = 0x9e3779b9;
  for (let i = 0; i < s.length; i++) { const c = s.charCodeAt(i); a = Math.imul(a ^ c, 16777619); b = Math.imul(b ^ c, 2246822519); }
  return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

/** A generator's result as [role, texture]: one texture ('tex') or { map, normalMap, roughnessMap }. */
export const rolesOf = (out) => (out?.isTexture ? [['tex', out]] : Object.entries(out || {}).filter(([, t]) => t?.isTexture));

/**
 * The package's baked maps: reads painted/maps.json and starts every file's download at once (before the renderer
 * exists); a file is transcoded by the map's KTX2 loader (`ktx()`: its promise, vegetation.js sharedKtx) when asked for.
 * Resolves null without a manifest. { keys(), entry(key) -> { r: { role: [w, h, srgb] }, s? } | undefined, texture(key, role) }
 */
export async function bakedMaps(base, ktx, get = fetch) {
  const dir = `${base}${BAKED_DIR}`;
  const man = await get(`${dir}maps.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null); // errors:ignore: no manifest = paint as before
  if (!man?.maps) return null;
  const files = new Map();
  for (const [key, e] of Object.entries(man.maps)) for (const role of Object.keys(e.r || {})) {
    const f = bakedFile(key, role), p = get(dir + f).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${f}: HTTP ${r.status}`))));
    p.catch(() => {}); // errors:ignore: a failed file is painted where it is asked for (createPainter)
    files.set(f, p);
  }
  return {
    keys: () => Object.keys(man.maps),
    entry: (key) => man.maps[key],
    texture: (key, role) => Promise.all([files.get(bakedFile(key, role)) || Promise.reject(new Error(`${bakedFile(key, role)}: not listed`)), ktx()])
      .then(([buf, l]) => new Promise((ok, no) => l.parse(buf, ok, no))),
  };
}

/** A stand-in texture of the baked picture's size and colour space, filled once its file is transcoded. Until then
 *  three draws its default texture (version 0), as for any picture still loading. */
function placeholder([w, h, srgb]) {
  const t = new THREE.CompressedTexture([], w, h, THREE.RGBAFormat);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 8;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
function fill(ph, t) {
  Object.assign(ph, { mipmaps: t.mipmaps, format: t.format, type: t.type, minFilter: t.minFilter });
  ph.image = { width: t.image.width, height: t.image.height };
  freeOnUpload(ph); // VIS-267 lossless: its levels' CPU copies go once on the graphics chip (Aurora 15 x 0.35 MB; a visitor's page)
  ph.needsUpdate = true;
}
/** The fallback: a painted canvas into the stand-in, as RGBA levels (flipped as the canvas's flipY would, box mips).
 *  VIS-267: three r187's WebGPU backend uploads RGBA levels of a CompressedTexture itself (#35082; checked by
 *  tests/unit/vendor-three.test.mjs); before it a slow first visit's late baked maps threw every frame. */
export function fillPainted(ph, t) {
  const c = t.image, w = c.width, h = c.height, px = c.getContext('2d').getImageData(0, 0, w, h).data, d = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) d.set(px.subarray((h - 1 - y) * w * 4, (h - y) * w * 4), y * w * 4);
  const mips = [{ data: d, width: w, height: h }];
  for (let lw = w, lh = h; lw > 1 || lh > 1;) {
    const nw = Math.max(1, lw >> 1), nh = Math.max(1, lh >> 1), p = mips[mips.length - 1].data, n = new Uint8Array(nw * nh * 4);
    const at = (x, y, k) => p[(Math.min(lh - 1, y) * lw + Math.min(lw - 1, x)) * 4 + k];
    for (let y = 0; y < nh; y++) for (let x = 0; x < nw; x++) for (let k = 0; k < 4; k++) n[(y * nw + x) * 4 + k] = (at(2 * x, 2 * y, k) + at(2 * x + 1, 2 * y, k) + at(2 * x, 2 * y + 1, k) + at(2 * x + 1, 2 * y + 1, k) + 2) >> 2;
    mips.push({ data: n, width: nw, height: nh });
    lw = nw; lh = nh;
  }
  fill(ph, { mipmaps: mips, format: THREE.RGBAFormat, type: THREE.UnsignedByteType, minFilter: THREE.LinearMipmapLinearFilter, image: { width: w, height: h } });
  t.dispose();
}

/**
 * The recipe cache of one generator family. seed = { get, set }: the shared random state the generators draw from
 * (materials.js), so a recipe that is not painted leaves it where painting would have (the manifest's `s`).
 * paint(recipe, make): make() paints; a recipe that is baked gets stand-ins at once, filled when the files arrive.
 * ready(): every fill asked for so far (main.js waits for it before textures are copied and before the first draw);
 * a recipe whose files are not in within waitMs (a stalled download, a transcoder that does not start) is painted.
 * 20 s (was 8): on a 3 Mbit/s line every recipe of Lumo missed 8 s and took its 4 bytes a texel (+38 MB on an iPhone) for
 * the same Ready; the files come while the opening's other downloads hold the line anyway.
 * unasked(): the baked recipes nothing asked for (a stale bake: main.js reports them).
 * Recording (tools/bake_material_maps.mjs sets globalThis.__mmsPaintLog = []): everything is painted and logged.
 */
export function createPainter(seed, { waitMs = 20000 } = {}) {
  const made = new Map(), waits = [];
  let baked = null;
  return {
    use(b) { baked = b || null; },
    ready: () => Promise.all(waits.splice(0)),
    unasked: () => (baked?.keys?.() || []).filter((k) => !made.has(k)),
    paint(recipe, make) {
      const key = recipeKey(recipe), had = made.get(key);
      if (had) { if (had.s !== undefined) seed.set(had.s); return had.out; }
      const log = globalThis.__mmsPaintLog, e = !log && baked?.entry(key), s0 = seed.get();
      let out;
      if (e) {
        const ph = Object.fromEntries(Object.entries(e.r).map(([role, m]) => [role, placeholder(m)]));
        out = ph.tex || ph;
        if (e.s !== undefined) seed.set(e.s);
        const b = baked;
        let late = false, timer = 0; // late: painted already (a file that arrives after that is left alone)
        const files = Promise.all(Object.keys(ph).map((role) => b.texture(key, role).then((t) => { if (!late) fill(ph[role], t); })));
        const limit = new Promise((_, no) => { timer = setTimeout(() => no(new Error(`no picture in ${waitMs / 1000} s`)), waitMs); });
        files.catch(() => {}); // errors:ignore: raced below
        waits.push(Promise.race([files, limit]).finally(() => clearTimeout(timer)).catch((err) => {
          late = true;
          console.warn(`[painted] ${key}: the baked picture did not load, painting it`, err);
          const s1 = seed.get();
          seed.set(s0);
          try { for (const [role, t] of rolesOf(make())) if (ph[role]) fillPainted(ph[role], t); } finally { seed.set(s1); }
        }));
      } else {
        out = make();
        log?.push({ key, recipe, s: seed.get() !== s0 ? seed.get() : undefined, out });
      }
      made.set(key, { out, s: seed.get() !== s0 ? seed.get() : undefined });
      return out;
    },
  };
}
